import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { PatientModelDto } from '@shared/models/rx-models/interfaces/patient-model-dto';
import { ShellQuery } from '@shared/store/shell/shell-query';
import type { Maybe } from '@/types/globals';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PatientAppUrlBuilderService } from '@modules/patient/services/patient-app-url-builder.service';
import { PatientAppInitService } from '@modules/patient/services/patient-app-init.service';
import { PatientAppIframeCommunicationService } from '@modules/patient/services/patient-app-iframe-communication.service';
import { MatDialogRef } from '@angular/material/dialog';
import { PatientAppPopUpComponent } from '@modules/patient/components/patient-app-popup/patient-app-popup.component';
import { PatientStore } from '@modules/patient/state/patient-store';

@Injectable()
export class RxIntuitivePatientHeaderFacade {
	private readonly patientExpandedSubject = new BehaviorSubject<boolean>(false);

	private openedPatientSnapshot: Maybe<PatientModelDto> = null;
	private isInitialUpdateAllowed = false;

	readonly patient$: Observable<Maybe<PatientModelDto>> = this.patientQuery.patient$;
	readonly isPatientCleared$: Observable<boolean> = this.patientQuery.isPatientCleared$;
	readonly dateFormat$: Observable<string> = this.shellQuery.dateFormat$;
	readonly patientUpdatedEvent$: Observable<boolean> = this.patientAppIframeCommunicationService.patientUpdatedEvent$;
	readonly initializePatientAppStreams$: Observable<void | MessageEvent<any> | MatDialogRef<PatientAppPopUpComponent>> = merge(
		this.patientAppInitService.initializePatientAppStreams$,
		this.patientAppIframeCommunicationService.patientPopupCommandHandler$
	);
	readonly patientExpanded$: Observable<boolean> = this.patientExpandedSubject.asObservable();
	readonly patientSelectedEvent$: Observable<boolean> = this.patientAppIframeCommunicationService.patientSelectedEvent$.pipe(
		tap(selected => (this.isInitialUpdateAllowed = selected))
	);
	readonly isPatientSectionReadOnly$: Observable<boolean> = this.shellQuery.getReadOnlyState('patient');

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly patientQuery: PatientQuery,
		private readonly sanitizer: DomSanitizer,
		private readonly patientAppUrlBuilderService: PatientAppUrlBuilderService,
		private readonly patientAppInitService: PatientAppInitService,
		private readonly patientAppIframeCommunicationService: PatientAppIframeCommunicationService,
		private readonly patientStore: PatientStore
	) {}

	get expandedPatientAppUrl$(): Observable<SafeResourceUrl | null> {
		return combineLatest([this.patientExpanded$, this.patient$]).pipe(
			map(([isExpanded]) => isExpanded),
			switchMap(isExpanded =>
				isExpanded
					? this.patientAppUrlBuilderService.getEditPatientAppUrl(true, this.isInitialUpdateAllowed).pipe(
							distinctUntilChanged(),
							map(url => this.sanitizer.bypassSecurityTrustResourceUrl(url))
						)
					: of(null)
			)
		);
	}

	toggleHeader(patient: Maybe<PatientModelDto>): void {
		const isExpanded = this.patientExpandedSubject.getValue();

		if (!isExpanded) {
			this.isInitialUpdateAllowed = false;
			this.openedPatientSnapshot = patient ? { ...patient } : null;
			this.patientExpandedSubject.next(true);

			return;
		}

		const currentStorePatient = this.patientQuery.patient;
		const openedPatientSnapshot = this.openedPatientSnapshot;
		const shouldRestorePatient = !!openedPatientSnapshot && !this.arePatientsEqual(currentStorePatient, openedPatientSnapshot);

		if (shouldRestorePatient) {
			// Keep patient store consistent if header is closed without confirming the changes.
			this.updatePatient(openedPatientSnapshot);
		}

		this.closeHeader();
	}

	closeHeader(): void {
		this.openedPatientSnapshot = null;
		this.patientExpandedSubject.next(false);
	}

	private updatePatient(patient: PatientModelDto): void {
		this.patientStore.updatePatient(patient, this.shellQuery.dateFormat, this.shellQuery.regulatoryDOBMask);
	}

	private arePatientsEqual(firstPatient: Maybe<PatientModelDto>, secondPatient: Maybe<PatientModelDto>): boolean {
		if (!firstPatient && !secondPatient) {
			return true;
		}

		if (!firstPatient || !secondPatient) {
			return false;
		}

		return firstPatient.Id === secondPatient.Id && firstPatient.UID === secondPatient.UID;
	}
}
